/**
 * Preload — loads the game's assets manifest, all locale dicts, and shows
 * a themed loading bar with rotating tips.
 *
 * The game's `assets/assets.json` is a flat map of `{ key: url }` plus
 * optional sections for spritesheets, atlases, audio, tilemaps, and bitmap
 * fonts. Format:
 *
 *   {
 *     "images": { "logo": "icons/logo.png" },
 *     "spritesheets": { "hero": { "url": "sprites/hero.png", "frameWidth": 32, "frameHeight": 32 } },
 *     "atlases": { "ui": { "url": "sprites/ui.png", "atlas": "sprites/ui.json" } },
 *     "audio": { "click": ["sounds/click.mp3", "sounds/click.ogg"] },
 *     "tilemaps": { "level1": "tilemaps/level1.json" },
 *     "bitmapFonts": { "main": { "png": "fonts/main.png", "xml": "fonts/main.xml" } },
 *     "tips": ["Hold to charge!", "Watch the wind direction"]
 *   }
 */
import { Theme } from '../ui/Theme.js';
import { ProgressBar } from '../ui/ProgressBar.js';

export class Preload extends Phaser.Scene {
  constructor() {
    super('Preload');
  }

  preload() {
    const reg = this.registry;
    const manifest = reg.get('manifest');
    const i18n = reg.get('i18n');
    const baseUrl = reg.get('baseUrl') || '';

    // Title.
    this.add.text(this.scale.width / 2, this.scale.height / 2 - 80, manifest.name, {
      fontFamily: Theme.font.family,
      fontSize: Theme.font.sizes.xl,
      color: Theme.colors.text,
      fontStyle: 'bold'
    }).setOrigin(0.5);

    // Loading bar.
    this._bar = new ProgressBar({
      scene: this,
      x: this.scale.width / 2,
      y: this.scale.height / 2,
      width: Math.min(360, this.scale.width - 80)
    });

    // Tip line.
    this._tipText = this.add.text(this.scale.width / 2, this.scale.height / 2 + 50, '', {
      fontFamily: Theme.font.family,
      fontSize: Theme.font.sizes.sm,
      color: Theme.colors.textDim,
      align: 'center',
      wordWrap: { width: this.scale.width - 40 }
    }).setOrigin(0.5);

    this.load.on('progress', (p) => this._bar.setValue(p));

    // Load locale dicts (one per declared language).
    for (const lang of (manifest.languages || ['en'])) {
      const url = baseUrl + 'assets/locales/' + lang + '.json';
      this.load.json('__locale_' + lang, url);
    }

    // Load the assets manifest itself.
    if (manifest.assetsManifest) {
      this.load.json('__assets_manifest', baseUrl + manifest.assetsManifest);
    }
  }

  create() {
    const reg = this.registry;
    const manifest = reg.get('manifest');
    const i18n = reg.get('i18n');
    const baseUrl = reg.get('baseUrl') || '';

    // Register loaded locale dicts with i18n.
    for (const lang of (manifest.languages || ['en'])) {
      const dict = this.cache.json.get('__locale_' + lang);
      if (dict) i18n.registerDict(lang, dict);
    }

    // Now load the actual gameplay assets declared by assets.json.
    const assets = this.cache.json.get('__assets_manifest');
    if (assets) {
      this._enqueueAssets(assets, baseUrl);
      // Reset the progress bar for the second load pass.
      this._bar.setValue(0);
      this.load.on('progress', (p) => this._bar.setValue(p));
      // Rotate tips.
      const tips = (assets.tips || []).map((t) => i18n.t(t));
      if (tips.length) {
        this._tipText.setText(tips[0]);
        let i = 0;
        this.time.addEvent({
          delay: 2400,
          loop: true,
          callback: () => {
            i = (i + 1) % tips.length;
            this._tipText.setText(tips[i]);
          }
        });
      }
      this.load.once('complete', () => this._done());
      this.load.start();
    } else {
      this._done();
    }
  }

  _enqueueAssets(assets, baseUrl) {
    const join = (p) => baseUrl + 'assets/' + p;

    if (assets.images) {
      for (const [key, url] of Object.entries(assets.images)) {
        this.load.image(key, join(url));
      }
    }
    if (assets.spritesheets) {
      for (const [key, def] of Object.entries(assets.spritesheets)) {
        this.load.spritesheet(key, join(def.url), {
          frameWidth: def.frameWidth,
          frameHeight: def.frameHeight
        });
      }
    }
    if (assets.atlases) {
      for (const [key, def] of Object.entries(assets.atlases)) {
        this.load.atlas(key, join(def.url), join(def.atlas));
      }
    }
    if (assets.audio) {
      for (const [key, urls] of Object.entries(assets.audio)) {
        const arr = Array.isArray(urls) ? urls : [urls];
        this.load.audio(key, arr.map(join));
      }
    }
    if (assets.tilemaps) {
      for (const [key, url] of Object.entries(assets.tilemaps)) {
        this.load.tilemapTiledJSON(key, join(url));
      }
    }
    if (assets.bitmapFonts) {
      for (const [key, def] of Object.entries(assets.bitmapFonts)) {
        this.load.bitmapFont(key, join(def.png), join(def.xml));
      }
    }
    if (assets.json) {
      for (const [key, url] of Object.entries(assets.json)) {
        this.load.json(key, join(url));
      }
    }
  }

  _done() {
    this.scene.start('MainMenu');
  }
}
